import { apiUrl } from "../../utils/api";
import type { AggregatedNoteDto } from "../../services/vorgangNoteService";
import { istLeererBlocktext } from "../notizbloecke/blockModell";

/**
 * Der zusammengeführte Notizstrom eines Kunden (PO 08.09.2026) — Tagebuch,
 * Vorgänge, To-dos, und dasselbe über Ansprechpartner, Verbund und Haushalt.
 *
 * Was hier ankommt, hat das Backend bereits Notiz für Notiz gegen das Leserecht
 * des jeweiligen Vorgangs geprüft; persönliche Vorgänge sind grundsätzlich
 * draußen. Das Frontend filtert also NICHT nach Sichtbarkeit — es teilt den
 * Strom nur auf: eigene Notizen in die Fläche ({@link nurEigene}), die aus
 * Vorgängen und To-dos ins Archiv ({@link nurFremde}).
 */

/** Woher eine Notiz stammt (Spiegel von NotizHerkunft im Backend). */
export interface NotizHerkunftDto {
    art: "kontakt" | "vorgang" | "todo";
    /** Kontakt, an dem die Notiz hängt — nicht zwingend der geöffnete Kunde. */
    kontaktId: number;
    kontaktName: string | null;
    vorgangId: number | null;
    vorgangTitel: string | null;
    todoId: number | null;
    todoTitel: string | null;
    /** Gesetzt, wenn die Notiz über einen anderen Kontakt hereinkommt. */
    ueber: string | null;
}

/**
 * Eine Notiz im Kundentagebuch. `source` kennt hier eine dritte Herkunft
 * (`'kontakt'`), damit der Composite-Key `source:id` über alle drei Tabellen
 * hinweg kollisionsfrei bleibt.
 */
export interface KundenNotizDto extends Omit<AggregatedNoteDto, "source"> {
    source: "kontakt" | "vorgang" | "todo";
    herkunft: NotizHerkunftDto;
    /** Fertige Beschriftung des Herkunfts-Badges; null = braucht keine. */
    herkunftLabel: string | null;
}

export async function ladeKundenNotizen(contactId: number): Promise<KundenNotizDto[]> {
    const res = await fetch(apiUrl(`kontakt/${contactId}/alle-notizen`));
    if (!res.ok) throw new Error(`Notizen laden fehlgeschlagen: ${res.status}`);
    return res.json();
}

/**
 * Die Notizen, die im Tagebuch selbst geschrieben wurden.
 *
 * ══ DIE TRENNUNG DES KUNDENTAGEBUCHS (PO 14.09.2026) ═══════════════════════
 * „kundentagebuch da nur neue notizen, notizen zu vorgang und todos im archiv
 * anzeigen". Die Notizfläche oben zeigt seitdem NUR diese hier; was aus einem
 * Vorgang oder einem To-do stammt ({@link nurFremde}), steht im „Verlauf /
 * Archiv" DESSELBEN Reiters — nicht in einem zweiten Archiv daneben.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Nur sie lassen sich hier auch bearbeiten — eine Vorgangs-Notiz gehört ihrem
 * Vorgang, und ein zweiter Bearbeitungsort für denselben Text wäre eine
 * Einladung zum Auseinanderlaufen. Im Archiv sind die fremden Notizen deshalb
 * nur lesbar; geändert wird im Vorgang.
 */
export function nurEigene(notizen: KundenNotizDto[]): KundenNotizDto[] {
    return notizen.filter((n) => n.source === "kontakt");
}

/**
 * Die Gegenprobe zu {@link nurEigene}: alles, was aus einem Vorgang oder einem
 * To-do hereinkommt. Genau diese Notizen zeigt das Archiv (Art „notiz").
 *
 * Beide Regeln stehen bewusst nebeneinander in EINER Datei: zusammen ergeben
 * sie den vollständigen Strom, und wer die eine ändert, sieht die andere.
 */
export function nurFremde(notizen: KundenNotizDto[]): KundenNotizDto[] {
    return notizen.filter((n) => n.source !== "kontakt");
}

/**
 * Die Notizen, die die Blockfläche wirklich ZEIGT.
 *
 * `bloeckeAus` lässt inhaltsleere Notizen weg (PO 13.09.2026: „leergeräumt
 * heißt gelöscht"). Der Zähler am Kopf muss dieselbe Regel anwenden — sonst
 * stünde dort „3", wo zwei Blöcke sichtbar sind, und genau diese Diskrepanz
 * sollte verschwinden. Neue leere Notizen entstehen seit demselben Tag nicht
 * mehr (die drei Notiz-Dienste löschen beim Leerräumen); was vorher entstand,
 * liegt aber noch in der Datenbank.
 */
export function sichtbareNotizen(notizen: KundenNotizDto[]): KundenNotizDto[] {
    return notizen.filter((n) => !istLeererBlocktext(n.text));
}
